import { useState, FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Process page data (Japanese)
// ─────────────────────────────────────────────
const processSteps = [
  {
    num: '01',
    icon: 'ri-discuss-line',
    title: 'ニーズの確認',
    desc: '営業担当者と初回打ち合わせを行い、ブランドのポジショニング・ターゲット層・想定販売チャネル・予算感をお伝えください。双方で協業の方向性を確認します。',
    duration: '1〜3営業日',
    color: '#4CAF50',
  },
  {
    num: '02',
    icon: 'ri-flask-line',
    title: '処方設計',
    desc: '研究開発チームがニーズに基づいた処方案を提案し、原料の機能・成分比率・初期見積もりをご説明します。ご確認いただいた後、試作サンプルを製造します。',
    duration: '5〜14営業日',
    color: '#43a047',
  },
  {
    num: '03',
    icon: 'ri-palette-line',
    title: 'パッケージデザイン',
    desc: 'パッケージ仕様のご提案、容器選定・ラベルレイアウトの確認サポート、食品表示規制に準拠したパッケージ文言の審査を行います。',
    duration: '7〜14営業日',
    color: '#388e3c',
  },
  {
    num: '04',
    icon: 'ri-verified-badge-line',
    title: '検査・認証',
    desc: 'サンプルを第三者機関に送付して安全性検査を実施。微生物・重金属・農薬残留などの完全なCoA（品質証明書）を発行し、合規上市を確保します。',
    duration: '10〜20営業日',
    color: '#2e7d32',
  },
  {
    num: '05',
    icon: 'ri-settings-3-line',
    title: '製造開始',
    desc: '契約締結・発注数量確認後、正式に量産に入ります。GMP工場での全工程管理により、各製造工程のポイントで品管担当者がチェックします。',
    duration: '4〜8週間',
    color: '#1b5e20',
  },
  {
    num: '06',
    icon: 'ri-truck-line',
    title: '納品完了',
    desc: '完成品が品質管理検収を通過した後、合意したスケジュールに従って出荷。完全な出荷書類を提供し、その後も継続的に技術サポートを行います。',
    duration: '契約条件に準ずる',
    color: '#4CAF50',
  },
];

const processHighlights = [
  { icon: 'ri-user-line', label: '専属担当者が全行程をサポート' },
  { icon: 'ri-shield-check-line', label: 'NDА秘密保持契約で処方を守る' },
  { icon: 'ri-time-line', label: '透明なスケジュールで進捗を把握' },
  { icon: 'ri-file-check-line', label: '完全な書類サポートでコンプライアンス保証' },
];

// ─────────────────────────────────────────────
// Services page data (Japanese)
// ─────────────────────────────────────────────
const coopServices = [
  {
    icon: 'ri-scales-3-line',
    tag: '01',
    title: '法規リスク管理',
    subtitle: '上市前のコンプライアンス確立 — ブランドの基盤を固める',
    desc: '食品・健康食品に関する法規情報を整理し、生産および販売上のリスク低減をサポートします。康永は食品法規の実務経験が豊富で、処方確定前に規制の事前審査を行い、原料の適法性と台湾TFDA規定への訴求文言適合を確認します。',
    points: [
      '正面リスト原料審査による成分の適法性確認',
      'ラベル表示文字と広告表現のコンプライアンスアドバイス',
      '健康食品認定（グリーンマーク）申請サポート',
      '輸出市場の法規差異に関するコンサルティング',
    ],
    img: 'https://readdy.ai/api/search-image?query=professional%20legal%20compliance%20document%20review%20health%20supplement%20regulatory%20affairs%20specialist%20checking%20ingredient%20list%20certification%20papers%20in%20clean%20office%2C%20minimal%20modern%20environment&width=640&height=420&seq=csvc-reg-01&orientation=landscape',
    cta: '法規リスクについて詳しく',
    ctaLink: '/ja/about/regulation',
  },
  {
    icon: 'ri-quill-pen-line',
    tag: '02',
    title: 'マーケティングコピー支援',
    subtitle: '法規を踏まえた、正確で魅力的な表現をお手伝い',
    desc: '食品関連広告の表現ルールを踏まえ、商品説明文やパッケージ文案の方向性をサポートします。多くのブランドはどの表現が適法でどれが問題になるか迷っています。康永は実務経験をもとに、製品の価値を伝えながら法規要件を満たす表現を一緒に考えます。',
    points: [
      'メインコピー・サブコピーの方向性提案',
      '適法な訴求表現のテンプレート提供',
      'SNS広告コピー（Facebook / Instagram / LINE）の審査サポート',
      'ECプラットフォーム商品説明文の最適化方向',
    ],
    img: 'https://readdy.ai/api/search-image?query=creative%20marketing%20team%20working%20on%20health%20supplement%20product%20packaging%20design%20copywriting%20brainstorming%20on%20whiteboard%2C%20professional%20agency%20environment%2C%20clean%20bright%20workspace&width=640&height=420&seq=csvc-copy-01&orientation=landscape',
    cta: 'コピーサービスについて相談',
    ctaLink: '/ja/cooperation/inquiry',
  },
  {
    icon: 'ri-ship-line',
    tag: '03',
    title: '輸入原料の代理調達',
    subtitle: '煩雑な手続きを省いて、高品質な原料を直接お届け',
    desc: '輸入原料の代理調達をサポートします。関連情報についてはお気軽にお問い合わせください。康永は長期にわたり国際原料サプライヤーと取引しており、輸入手続き・関税申告・品質認証要件に精通しています。',
    points: [
      '日本・アメリカ・ヨーロッパなどの認定原料調達',
      '原料規格書と安全認証書類の確認サポート',
      'まとめ買いによる優遇価格の受け渡し',
      '輸入申告・検査関連書類のサポート',
    ],
    img: 'https://readdy.ai/api/search-image?query=international%20raw%20material%20import%20shipping%20containers%20herbs%20botanical%20extracts%20high%20quality%20natural%20ingredients%20sourcing%20global%20supply%20chain%2C%20professional%20logistics%20health%20supplement%20industry&width=640&height=420&seq=csvc-import-01&orientation=landscape',
    cta: '代理調達サービスについて相談',
    ctaLink: '/ja/cooperation/inquiry',
  },
  {
    icon: 'ri-rocket-line',
    tag: '04',
    title: 'スムーズな市場導入',
    subtitle: '小ロット対応で迅速な市場検証と製品ライン拡張',
    desc: '成熟した半製品と小ロット対応により、ブランドが低リスクで商品ラインを展開しやすくなります。新市場のテストや製品ラインの拡充を検討しているブランドに最適。最初から大量在庫を抱える必要はなく、小ロットで迅速に上市し、販売反応を確認してから量産規模を決定することで在庫リスクを抑えられます。',
    points: [
      'カプセル／錠剤は3,000本からご対応可能',
      '成熟した処方により開発期間を短縮し、迅速上市を実現',
      '複数の既存処方からお選びいただけます',
      '市場検証後はシームレスに量産へ移行',
    ],
    img: 'https://readdy.ai/api/search-image?query=small%20batch%20supplement%20product%20launch%20health%20brand%20new%20product%20launch%20fast%20to%20market%20concept%20bottles%20capsules%20arranged%20neatly%2C%20clean%20minimal%20product%20photography%20startup%20brand&width=640&height=420&seq=csvc-launch-01&orientation=landscape',
    cta: '小ロット注文について詳しく',
    ctaLink: '/ja/cooperation/inquiry',
  },
];

// ─────────────────────────────────────────────
// FAQ page data (Japanese)
// ─────────────────────────────────────────────
const faqCategories = [
  {
    id: 'brand',
    icon: 'ri-store-line',
    label: 'ブランドとマーケティング',
    faqs: [
      {
        q: 'ブランドがなくても康永に委託できますか？',
        a: 'もちろんです！康永はODM（委託設計製造）サービスを提供しています。製品の方向性とターゲット層をお伝えいただければ、研究開発チームが処方設計・パッケージ提案・コピー方向まで一歩ずつ完成させるお手伝いをします。技術的な背景知識は一切不要です。',
      },
      {
        q: '選べる既成処方はありますか？',
        a: 'はい。康永には直接使用できる成熟した既存処方が多数あります。既存処方をベースに軽微な調整も可能で、開発期間を大幅に短縮し研究開発コストを削減できます。迅速な上市を希望するブランドに最適です。',
      },
      {
        q: '市場テスト用に1品目だけ少量作りたいのですが、可能ですか？',
        a: '全く問題ありません。康永は小ロット受注に対応しており、カプセル・錠剤は最低3,000本から。最小限の在庫リスクで先に市場の反応を確かめ、見込みがあれば規模を拡大できます。',
      },
    ],
  },
  {
    id: 'risk',
    icon: 'ri-shield-check-line',
    label: '小ロット・リスク管理',
    faqs: [
      {
        q: '最小発注量（MOQ）はどのくらいですか？',
        a: '剤型によって異なります。カプセル・錠剤は約3,000〜5,000本から、粉末スティック・飲料は約5,000包からとなります。特別なご要望がある場合は営業担当者にご相談ください。できる限り対応いたします。',
      },
      {
        q: '少量試作はコストが高くなりますか？',
        a: '少量試作の単位コストは量産より高くなるのが製造業全般の通常です。ただし康永はすべての見積もりでコスト構造を明確にお伝えしますので、市場実現可能性の評価に明確な数字を活用いただけます。',
      },
      {
        q: '試作後に市場の反応が悪かった場合、余った在庫はどうすればよいですか？',
        a: '少量試作時に販売戦略を設定し、市場検証を主目的にすることをお勧めします。康永も適切な試作量の見積もりをサポートし、過剰在庫を防ぎます。後から処方調整が必要になった場合も改善方向のサポートが可能です。',
      },
    ],
  },
  {
    id: 'regulation',
    icon: 'ri-file-text-line',
    label: '法規と合法マーケティング',
    faqs: [
      {
        q: '製品の訴求文言にはどんな制限がありますか？',
        a: '台湾の規制では、一般食品による「疾病の予防・治療・軽減・診断」に関する表現を明確に禁止しています。例えば「降血圧」「不眠治療」などは違反表現です。「正常な代謝維持に役立つ」「体質調整を助ける」などの適法表現への変更をお勧めします。康永が文案のコンプライアンス審査をサポートします。',
      },
      {
        q: '健康食品認定（グリーンマーク）の申請サポートはできますか？',
        a: 'サポート可能です。康永は健康食品認定申請フローに精通しており、安全性評価・有効性研究などの必要書類準備をサポートします。申請には一定の時間が必要なため、製品企画の初期から考慮されることをお勧めします。',
      },
      {
        q: 'KOL・インフルエンサー協業宣伝の注意点は？',
        a: '食品安全衛生管理法により、インターネット・SNS広告も食品広告規制の適用対象です。KOLによる使用体験が具体的な効果に言及すれば広告とみなされ、ブランド側と発信者の両者が処罰される可能性があります。康永が法規準拠の協業説明テンプレート作成をサポートします。',
      },
    ],
  },
  {
    id: 'channel',
    icon: 'ri-shopping-bag-line',
    label: 'チャネルと販売サポート',
    faqs: [
      {
        q: 'チャネル開拓のサポートはできますか？',
        a: '康永の主要サービス範囲はOEM製造であり、チャネル開拓はブランド側の業務となります。ただし業界のチャネル情報の共有や、各チャネルの製品仕様・パッケージ・書類要件のご説明など、上場準備のサポートは可能です。',
      },
      {
        q: 'EC上場には何の書類が必要ですか？',
        a: '主要ECプラットフォームは通常、成分表示・原料合格証明・食品安全管理計画またはGMP証明書・製品検査報告書を求めます。康永はバッチごとに完全なCoA検査報告書を発行し、審査通過をサポートします。',
      },
      {
        q: '実店舗の薬局チャネルには特別な要件がありますか？',
        a: '薬局チャネルは品質文書への要求が高く、通常GMP工場証明書・完全なバッチ検査報告書・安定性試験データの提供が求められます。康永の生産設備と書類はいずれも薬局チャネルの審査要件を満たしています。',
      },
    ],
  },
  {
    id: 'production',
    icon: 'ri-settings-3-line',
    label: '製造・納期・品質',
    faqs: [
      {
        q: '契約から製品受取まで、どのくらいかかりますか？',
        a: '一般的なフローでは、初回相談からサンプル完成まで約3〜6週間。サンプル確認後の量産は通常4〜8週間かかります（注文規模・処方の複雑さによる）。スケジュールは契約書に明記しますので、進捗を明確に把握できます。',
      },
      {
        q: 'バッチごとの品質均一性はどう確保しますか？',
        a: '康永は完全な製造工程管理SOPを構築しており、バッチごとに品管担当者が全行程を監視。中間管理と完成品全項目検査を実施し、CoA発行後にのみ出荷。すべてのバッチで安定した品質を確保します。',
      },
      {
        q: '受け取った製品に問題があった場合はどうなりますか？',
        a: '康永はバッチごとに完全なトレーサビリティシステムを構築しています。品質上の懸念が生じた場合は直ちに調査フローを開始し、原因を追跡して解決策を提案します。品質は最も重要なコミットメントであり、すべての問題に真剣に向き合います。',
      },
    ],
  },
  {
    id: 'trust',
    icon: 'ri-shake-hands-line',
    label: '協業と信頼',
    faqs: [
      {
        q: '処方が漏洩することはありませんか？',
        a: '絶対にありません。康永は協業開始前に厳格なNDA（秘密保持契約）を締結します。処方・事業計画・すべての技術情報は完全に法的保護を受けており、第三者への提供は一切行いません。',
      },
      {
        q: '健康食品は初めてです。対応していただけますか？',
        a: 'もちろんです！康永は新規ブランドクライアントを大歓迎しています。営業チームには豊富な新規クライアント指導経験があり、ゼロからのプロセスを一歩ずつご案内します。健康食品の専門知識は一切不要です。',
      },
      {
        q: '協業を決める前に工場を見学できますか？',
        a: '工場見学を歓迎します。施設・生産環境を実際にご覧いただくことが、信頼構築への最も直接的な方法です。営業担当者にご連絡いただき、訪問時間をご予約ください。',
      },
    ],
  },
  {
    id: 'export',
    icon: 'ri-global-line',
    label: '海外販売・越境市場',
    faqs: [
      {
        q: '他の国への輸出サポートはできますか？',
        a: 'はい。康永は多国籍輸出経験を持ち、台湾からの輸出に必要な書類フォーマットを熟知しています。ターゲット市場ごとに異なる輸入要件があるため、製品企画の初期から輸出ニーズをお知らせいただくと、対応する書類と処方調整を事前に準備できます。',
      },
      {
        q: '東南アジアへの輸出にはどんな認証が必要ですか？',
        a: '各国で要件が異なります。マレーシアはKKMへの輸入許可申請、タイはタイFDAへの登録、ベトナムは保健省の輸入許可書が必要です。康永はターゲット市場の書類要件を理解するサポートと、必要な工場認証資料を提供します。',
      },
      {
        q: '私の製品成分が他の国で不適合かもしれない場合はどうすればよいですか？',
        a: '国によって原料の安全性と1日摂取量上限に関する規制が異なります。台湾では適合していても他市場では不適合の場合があります。輸出先が確定した後、ターゲット市場の法規実現可能性評価を実施することをお勧めします。康永が初期コンサルティングを提供できます。',
      },
    ],
  },
];

// ─────────────────────────────────────────────
// Inquiry page data (Japanese)
// ─────────────────────────────────────────────
const productTypes = [
  'ハードカプセル',
  'ソフトジェルカプセル',
  '錠剤',
  'チュアブル錠',
  '粉末スティック・顆粒',
  '機能性飲料',
  '経口液',
  'グミ',
  'その他の剤型（未定）',
];

const quantityOptions = [
  '3,000本未満（サンプル・試作）',
  '3,000〜10,000本',
  '10,000〜30,000本',
  '30,000本以上',
  '未定 — アドバイスが必要',
];

const suitableFor = [
  { icon: 'ri-store-2-line', title: '新興健康食品ブランド', desc: 'ゼロからブランドを立ち上げたいブランドで、小ロット対応のOEMパートナーを探している。' },
  { icon: 'ri-shopping-bag-line', title: 'EC・小売流通業者', desc: '安定したチャネルを持ち、自社ブランド製品ラインの拡充や取り扱い品目の追加を検討している。' },
  { icon: 'ri-global-line', title: '輸出志向のブランド', desc: '海外市場への参入を計画しており、輸出書類サポート能力のあるOEM工場を必要としている。' },
  { icon: 'ri-team-line', title: 'R&Dサポートが必要なブランド', desc: '製品コンセプトはあるが処方技術がなく、ODM設計・開発サービスを必要としている。' },
];

const inquiryProcessReminders = [
  { step: '01', icon: 'ri-send-plane-line', text: 'フォーム送信またはLINE追加 — 営業が1営業日以内に返答' },
  { step: '02', icon: 'ri-discuss-line', text: '詳細なヒアリングで製品方向と協業可能性を確認' },
  { step: '03', icon: 'ri-draft-line', text: '処方提案書と初期見積もりを提供' },
  { step: '04', icon: 'ri-test-tube-line', text: '試作サンプルを製造し、品質確認後に正式な協業へ' },
];

const FORM_URL = 'https://readdy.ai/api/form/d7kd2a263r16okiqphkg';

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function JaCooperationSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/ja/cooperation/process': {
      title: '協業フロー｜6ステップ OEM ODM 提携｜康永生技',
      description: '康永生技の6ステップ協業フロー：ニーズ確認、処方設計、パッケージデザイン、検査・認証、製造開始、納品完了。明確で透明なプロセスです。',
      keywords: 'OEM協業フロー,ODMプロセス,健康食品製造ステップ,提携台湾,康永フロー',
    },
    '/ja/cooperation/services': {
      title: 'その他サービス｜法規リスク・コピー・原料調達｜康永生技',
      description: '康永生技の付加価値サービス：法規リスク管理、マーケティングコピー支援、輸入原料代理調達、スムーズな市場導入 — 製造を超えたサポートを提供します。',
      keywords: '法規サービス,健康食品コピー,原料調達,市場導入,付加価値OEMサービス',
    },
    '/ja/cooperation/faq': {
      title: 'よくある質問｜健康食品OEM ODM Q&A｜康永生技',
      description: '健康食品OEM/ODMに関するよくある質問：ブランド構築、MOQ、法規対応、販路、製造リードタイム、輸出、パートナーシップの信頼など。',
      keywords: 'OEM FAQ,ODM FAQ,健康食品質問,MOQ,サプリメント法規,ブランドパートナーシップFAQ',
    },
    '/ja/cooperation/inquiry': {
      title: '今すぐ相談｜OEM ODM 健康食品お問い合わせ｜康永生技',
      description: '康永生技にOEM/ODM健康食品のお問い合わせを送信。担当者が1営業日以内に返答し、最適な協業方法を評価します。',
      keywords: 'OEM問い合わせ,ODM問い合わせ,健康食品相談,製造お問い合わせ,康永お問い合わせフォーム',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/ja/cooperation/process'];
  useSEO({ ...currentSeo, lang: 'ja', canonicalPath: path });

  const [activeCategory, setActiveCategory] = useState('brand');
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const currentCat = faqCategories.find((c) => c.id === activeCategory)!;

  const handleCatChange = (id: string) => {
    setActiveCategory(id);
    setOpenFaqIdx(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  const subNavItems = [
    { path: '/ja/cooperation/process', label: '協業フロー', desc: 'ニーズ確認から納品まで、各ステップを明確に。' },
    { path: '/ja/cooperation/services', label: 'その他サービス', desc: '法規リスク・コピー・原料調達など付加価値サービス。' },
    { path: '/ja/cooperation/faq', label: 'よくある質問', desc: 'ブランド・MOQ・法規・チャネルなど、よくある疑問をまとめました。' },
    { path: '/ja/cooperation/inquiry', label: '今すぐ相談', desc: 'お問い合わせフォームを記入して最初の一歩を踏み出しましょう。' },
  ];

  return (
    <JaLayout>

      {/* ── 協業フロー ── */}
      {path === '/ja/cooperation/process' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20team%20planning%20project%20roadmap%20on%20whiteboard%20in%20modern%20bright%20office%2C%20health%20supplement%20brand%20collaboration%20meeting%2C%20clean%20minimal%20workspace%20with%20green%20plants%2C%20natural%20daylight&width=1920&height=420&seq=proc-hero-bg-01&orientation=landscape"
                alt="協業フロー"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/partnership" className="hover:text-white cursor-pointer">提携について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">協業フロー</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">提携について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">協業フロー</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  ニーズの確認から納品完了まで、明確でコントロールしやすいフローでブランドの製品開発をサポートします。
                </p>
              </div>
            </div>
          </section>

          {/* Highlights bar */}
          <section className="bg-white border-b border-gray-100 py-5">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {processHighlights.map((h) => (
                  <div key={h.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className={`${h.icon} text-[#4CAF50] text-base`} />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.875rem] font-medium leading-snug">{h.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Steps */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">6ステップフロー</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">相談から納品まで、すべてのステップが明確</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  すべてのステップで専門担当者が伴走し、協業全体を通じて安心してお任せいただけます
                </p>
              </div>

              {/* Desktop flow */}
              <div className="hidden lg:block">
                <div className="grid grid-cols-3 gap-0 mb-0">
                  {processSteps.slice(0, 3).map((s, idx) => (
                    <div key={s.num} className="relative flex flex-col items-center">
                      {idx < 2 && <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />}
                      <div className="relative z-10 flex flex-col items-center px-5 w-full">
                        <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                          <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                          <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{s.num}</span>
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                        <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                        <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                          <i className="ri-time-line text-sm" />{s.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pr-[16.67%] my-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                    <i className="ri-arrow-down-line text-[#4CAF50] text-lg" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-0">
                  {processSteps.slice(3, 6).reverse().map((s, idx) => (
                    <div key={s.num} className="relative flex flex-col items-center">
                      {idx < 2 && <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />}
                      <div className="relative z-10 flex flex-col items-center px-5 w-full">
                        <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                          <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                          <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{s.num}</span>
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                        <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                        <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                          <i className="ri-time-line text-sm" />{s.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile vertical flow */}
              <div className="lg:hidden space-y-0">
                {processSteps.map((s, idx) => (
                  <div key={s.num} className="flex gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                        <i className={`${s.icon} text-xl text-[#4CAF50]`} />
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                      </div>
                      {idx < processSteps.length - 1 && <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[40px]" />}
                    </div>
                    <div className="pb-8 flex-1 min-w-0">
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1.5">{s.title}</h3>
                      <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-2">{s.desc}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                        <i className="ri-time-line text-sm" />{s.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Detail cards */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-12">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">各ステップの詳細</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">すべてのステップで私たちが伴走します</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  初回相談から完成品納品まで、康永の営業・研究開発チームが全行程をサポートします
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {processSteps.map((s) => (
                  <div key={s.num} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[#4CAF50] font-bold text-[1.75rem] leading-none opacity-20">{s.num}</span>
                      <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100">
                        <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{s.desc}</p>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-[#4CAF50] text-sm" />
                      <span className="text-[#4CAF50] text-[0.8125rem] font-medium">{s.duration}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">準備はできていますか？</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                一通のメッセージまたはお電話だけで最初の一歩が踏み出せます。康永の営業チームは1営業日以内に返答します。
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  相談を始める
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/ja/cooperation/faq" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  よくある質問を見る
                </Link>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">提携関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'その他サービス', desc: '法規リスク・コピー・原料調達など、付加価値サービスをご確認ください。', href: '/ja/cooperation/services' },
                  { title: 'よくある質問', desc: 'ブランド・MOQ・法規・チャネルなどよくある疑問をまとめました。', href: '/ja/cooperation/faq' },
                  { title: '今すぐ相談', desc: 'お問い合わせフォームを記入して最初の一歩を踏み出しましょう。', href: '/ja/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── その他サービス ── */}
      {path === '/ja/cooperation/services' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20professional%20services%20health%20biotech%20company%20offering%20multiple%20solutions%20consultation%20meeting%20team%20of%20experts%20in%20modern%20bright%20office%2C%20collaborative%20atmosphere%20clean%20minimal%20interior&width=1920&height=420&seq=csvc-hero-bg-01&orientation=landscape"
                alt="その他サービス"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/partnership" className="hover:text-white cursor-pointer">提携について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">その他サービス</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">提携について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">その他サービス</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  OEM製造以外にも、康永生技は法規・コピー・上市まで、ブランドを全方位でサポートする付加価値サービスを提供しています。
                </p>
              </div>
            </div>
          </section>

          {/* Services */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-20">
              {coopServices.map((svc, idx) => (
                <div key={svc.tag} className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}>
                  <div className="w-full lg:w-1/2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[#4CAF50] font-bold text-[1.75rem] leading-none opacity-20">{svc.tag}</span>
                      <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl border border-green-200">
                        <i className={`${svc.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-2">{svc.title}</h2>
                    <p className="text-[#4CAF50] font-medium text-[1rem] mb-5">{svc.subtitle}</p>
                    <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{svc.desc}</p>
                    <ul className="space-y-3 mb-7">
                      {svc.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={svc.ctaLink} className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                      {svc.cta}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-2xl overflow-hidden">
                      <img src={svc.img} alt={svc.title} className="w-full h-[300px] lg:h-[360px] object-cover object-top" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Summary cards */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-10">
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2rem] mb-3">製造だけでなく、全方位パートナー</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  法規からコピー、原料調達から市場投入まで、康永がすべての工程でプロとしてサポートします
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {coopServices.map((svc) => (
                  <div key={svc.tag} className="bg-white rounded-xl p-5 border border-gray-100 text-center hover:border-green-200 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-3 border border-green-100">
                      <i className={`${svc.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem]">{svc.title}</h3>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  その他サービスについて相談
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">提携関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: '協業フロー', desc: 'ニーズ確認から納品まで、各ステップを明確に把握できます。', href: '/ja/cooperation/process' },
                  { title: '法規リスク', desc: '健康食品法規の要点を把握し、源流からリスクを防止。', href: '/ja/about/regulation' },
                  { title: 'パッケージング', desc: 'パッケージ材・ビジュアルデザイン・法規表示のワンストップソリューション。', href: '/ja/services/packaging' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── よくある質問 ── */}
      {path === '/ja/cooperation/faq' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[320px] md:min-h-[380px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=person%20reading%20FAQ%20document%20health%20supplement%20product%20brochure%20on%20clean%20white%20desk%20with%20green%20plant%2C%20professional%20customer%20support%20service%20concept%2C%20bright%20natural%20lighting%20minimal&width=1920&height=380&seq=faq-hero-bg-01&orientation=landscape"
                alt="よくある質問"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-24">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/partnership" className="hover:text-white cursor-pointer">提携について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">よくある質問</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">提携について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">よくある質問</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  ブランドクライアントからよくいただく質問をテーマ別に整理しました。検索しやすいようにカテゴリー分けしています。
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Layout */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left: categories */}
                <div className="w-full lg:w-[260px] flex-shrink-0">
                  <h2 className="text-[#1a2e1a] font-bold text-[1rem] mb-4 px-1">カテゴリー</h2>
                  <div className="flex flex-row lg:flex-col flex-wrap gap-2">
                    {faqCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCatChange(cat.id)}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-left transition-all duration-200 cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'bg-[#4CAF50] text-white' : 'bg-white text-gray-600 border border-gray-100 hover:border-green-200 hover:text-[#4CAF50]'}`}
                      >
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-sm`} />
                        </div>
                        <span className="text-[0.875rem] font-medium">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Right: FAQs */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#4CAF50] rounded-xl">
                      <i className={`${currentCat.icon} text-white text-base`} />
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.25rem]">{currentCat.label}</h2>
                    <span className="text-gray-400 text-[0.8125rem]">{currentCat.faqs.length}つの質問</span>
                  </div>
                  <div className="space-y-3">
                    {currentCat.faqs.map((faq, idx) => (
                      <div
                        key={idx}
                        className={`bg-white rounded-xl border overflow-hidden transition-all duration-200 ${openFaqIdx === idx ? 'border-[#4CAF50]/40' : 'border-gray-100 hover:border-green-200'}`}
                      >
                        <button
                          className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer"
                          onClick={() => setOpenFaqIdx(openFaqIdx === idx ? null : idx)}
                        >
                          <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaqIdx === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${openFaqIdx === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                            <i className={`text-sm ${openFaqIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                          </div>
                        </button>
                        {openFaqIdx === idx && (
                          <div className="px-6 pb-5 border-t border-gray-50">
                            <p className="text-gray-600 text-[0.9375rem] leading-[1.8] pt-4">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Still have questions */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-[#f0faf0] border border-green-100 rounded-3xl px-8 lg:px-14 py-12 flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-3">お探しの質問が見つかりませんでしたか？</h2>
                  <p className="text-gray-500 text-[1.0625rem] leading-[1.75]">
                    康永生技の営業チームに直接ご連絡ください。1営業日以内にすべてのご質問にお答えします。
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 flex-shrink-0">
                  <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    直接お問い合わせ
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    <i className="ri-line-fill" /> LINEで素早く相談
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">提携関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: '協業フロー', desc: 'ニーズ確認から納品まで、各ステップを明確に把握できます。', href: '/ja/cooperation/process' },
                  { title: '今すぐ相談', desc: 'お問い合わせフォームを記入して、康永の専門担当者が1営業日以内に返答します。', href: '/ja/cooperation/inquiry' },
                  { title: 'お問い合わせフォーム', desc: 'お問い合わせフォームを記入して協業を始めましょう。', href: '/ja/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── 今すぐ相談 ── */}
      {path === '/ja/cooperation/inquiry' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[340px] md:min-h-[400px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20consultation%20handshake%20meeting%20health%20supplement%20brand%20partnership%2C%20modern%20bright%20office%20with%20green%20plants%2C%20warm%20welcoming%20atmosphere%2C%20clean%20minimal%20workspace%20natural%20light&width=1920&height=400&seq=inquiry-hero-bg-01&orientation=landscape"
                alt="今すぐ相談"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/ja" className="hover:text-white cursor-pointer">ホーム</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/ja/partnership" className="hover:text-white cursor-pointer">提携について</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">今すぐ相談</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">提携について</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">今すぐ相談</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  ご要望をお知らせください。康永生技が最適なODM / OEM協業の進め方を評価・提案します。
                </p>
              </div>
            </div>
          </section>

          {/* Suitable for */}
          <section className="py-14 bg-white border-b border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-10">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">対象となるブランド</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] mb-3">どんなブランドに適していますか？</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  立ち上げたばかりの新ブランドから、製品ラインの拡充を検討している成熟ブランドまで、康永はそれぞれに対応したプランをご用意しています
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {suitableFor.map((s) => (
                  <div key={s.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                      <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Main content: form + contact info */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

                {/* Left: contact info */}
                <div className="w-full lg:w-[320px] flex-shrink-0 space-y-5">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-2">その他の連絡方法</h2>

                  {/* LINE */}
                  <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-line-fill text-[#4CAF50] text-xl" />
                      <p className="text-[#1a2e1a] font-bold text-[1rem]">LINE公式アカウント</p>
                    </div>
                    <p className="text-[#1a2e1a] text-[0.875rem] font-medium mb-1">@126uwznp — 素早い相談</p>
                    <p className="text-gray-600 text-[0.8125rem] leading-[1.75] mb-4">
                      康永生技のLINE公式アカウントに参加してリアルタイムでお問い合わせいただけます。担当者が素早く返答します。
                    </p>
                    <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full">
                      <i className="ri-line-fill text-lg" /> 今すぐLINEに参加
                    </a>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className="ri-phone-line text-[#4CAF50] text-lg" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-[0.75rem] mb-0.5">電話番号</p>
                      <a href="tel:+886422029858" className="text-[#1a2e1a] font-semibold text-[0.9375rem] hover:text-[#4CAF50] transition-colors cursor-pointer">+886-4-2202-9858</a>
                      <p className="text-gray-400 text-[0.75rem] mt-1">月〜金 09:00〜18:00（UTC+8）</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className="ri-mail-line text-[#4CAF50] text-lg" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-[0.75rem] mb-0.5">メールアドレス</p>
                      <a href="mailto:natural1070606@gmail.com" className="text-[#1a2e1a] font-semibold text-[0.875rem] hover:text-[#4CAF50] transition-colors cursor-pointer break-all">natural1070606@gmail.com</a>
                    </div>
                  </div>

                  {/* Process reminder */}
                  <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-4 flex items-center gap-2">
                      <i className="ri-map-pin-time-line text-[#4CAF50]" /> お問い合わせ後のフロー
                    </p>
                    <div className="space-y-3">
                      {inquiryProcessReminders.map((r) => (
                        <div key={r.step} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center bg-green-50 text-[#4CAF50] text-[0.6875rem] font-bold rounded-full flex-shrink-0 mt-0.5 border border-green-100">{r.step}</span>
                          <span className="text-gray-600 text-[0.8125rem] leading-[1.75]">{r.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: form */}
                <div className="w-full lg:flex-1">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-6">お問い合わせ内容を記入</h2>

                  {submitState === 'success' ? (
                    <div className="flex flex-col items-center text-center bg-white rounded-2xl border border-green-100 overflow-hidden">
                      <div className="w-full px-8 pt-12 pb-8">
                        <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">お問い合わせを受け付けました。ありがとうございます！</h3>
                        <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                          康永生技は1営業日以内にご連絡し、製品の方向性・剤型・予定数量・協業方式の確認をサポートします。
                        </p>
                      </div>
                      <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                        <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">より早い返答をご希望の場合</p>
                        <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">LINEに今すぐ参加して、担当者にご要望を確認していただくことも可能です。</p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                          <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
                            <i className="ri-line-fill text-lg" /> 今すぐLINEに参加
                          </a>
                          <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@ ID: @126uwznp</span>
                        </div>
                      </div>
                      <div className="px-8 py-5">
                        <button onClick={() => setSubmitState('idle')} className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer">
                          再度送信する
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-7 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">お名前 <span className="text-red-500">*</span></label>
                          <input name="contact_name" type="text" required placeholder="お名前をご記入ください" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">会社名・ブランド名</label>
                          <input name="company_name" type="text" placeholder="会社名またはブランド名をご記入ください" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">電話番号 <span className="text-red-500">*</span></label>
                          <input name="phone" type="tel" required placeholder="連絡先電話番号をご記入ください" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">メールアドレス <span className="text-red-500">*</span></label>
                          <input name="email" type="email" required placeholder="メールアドレスをご記入ください" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">ご希望の製品タイプ</label>
                        <select name="product_type" defaultValue="" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer">
                          <option value="" disabled>製品タイプをお選びください</option>
                          {productTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">予定数量</label>
                        <select name="quantity" defaultValue="" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer">
                          <option value="" disabled>予定数量をお選びください</option>
                          {quantityOptions.map((q) => <option key={q} value={q}>{q}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                          お問い合わせ内容 <span className="text-gray-400 text-[0.75rem] font-normal">({charCount}/500)</span>
                        </label>
                        <textarea
                          name="requirement"
                          rows={5}
                          maxLength={500}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          placeholder="ご要望を簡単にご記入ください（例：製品の方向性・健康訴求・ターゲット層・協業方式などの補足情報）"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                        />
                        {charCount > 500 && <p className="text-red-500 text-[0.75rem] mt-1">お問い合わせ内容は500文字以内でご記入ください</p>}
                      </div>
                      <button type="submit" disabled={submitState === 'loading' || charCount > 500} className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer">
                        {submitState === 'loading' ? (<><i className="ri-loader-4-line animate-spin text-lg" /> 送信中…</>) : (<><i className="ri-send-plane-line text-lg" /> お問い合わせを送信</>)}
                      </button>
                      {submitState === 'error' && <p className="text-red-500 text-[0.875rem] text-center">送信に失敗しました。しばらくしてから再度お試しいただくか、直接お電話ください。</p>}
                      <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                        フォームを送信することで、ご要求されたサービス返答を提供するために個人情報をプライバシーポリシーに従って処理することに同意したものとみなされます。
                      </p>
                      <div className="border-t border-gray-100 pt-5 mt-1">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f7faf7] rounded-xl px-5 py-4 border border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                              <i className="ri-line-fill text-[#4CAF50] text-lg" />
                            </div>
                            <div>
                              <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">より早い確認をご希望ですか？</p>
                              <p className="text-gray-500 text-[0.8125rem]">送信後にLINEに参加 — LINE@ ID: <span className="text-[#4CAF50] font-medium">@126uwznp</span></p>
                            </div>
                          </div>
                          <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto">
                            <i className="ri-line-fill" /> 今すぐLINEに参加
                          </a>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">提携関連ページ</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'よくある質問', desc: 'ブランド・MOQ・法規・チャネルなどよくある疑問をまとめました。', href: '/ja/cooperation/faq' },
                  { title: 'OEMサービス', desc: '人気処方・剤型・パッケージデザインについて詳しくご確認ください。', href: '/ja/services/hot-formulas' },
                  { title: 'LINEで相談', desc: 'LINEに参加して素早い相談を — 担当者がリアルタイムで返答します。', href: 'https://lin.ee/X7Hcpf4' },
                ].map((r) => (
                  r.href.startsWith('http')
                    ? <a key={r.href} href={r.href} rel="nofollow" className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                        <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                        <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                      </a>
                    : <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                        <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                        <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                      </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FALLBACK ── */}
      {!['process', 'services', 'faq', 'inquiry'].some(p => path.includes(p)) && (
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {subNavItems.map((item) => (
                <Link key={item.path} to={item.path} className="bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer block">
                  <p className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.label}</p>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </JaLayout>
  );
}
